import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { filter, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnInit {
  static readonly ROUTE_DATA_BREADCRUMB = 'breadcrumb';
  public breadcrumbs: IBreadCrumb[];
  currentRoute: string;
  isBreadcrumbHidden = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    this.breadcrumbs = this.buildBreadCrumb(this.activatedRoute.root);
  }

  ngOnInit(): void {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      distinctUntilChanged(),
    ).subscribe(() => {
      this.breadcrumbs = this.buildBreadCrumb(this.activatedRoute.root);
    });
  }

  buildBreadCrumb(route: ActivatedRoute, url = '', breadcrumbs: IBreadCrumb[] = []): IBreadCrumb[] {
    // If no routeConfig is avalailable we are on the root path
    let label = route.routeConfig?.data?.breadcrumb || '';
    let path = route.routeConfig && route.routeConfig.data ? route.routeConfig.path : '';
    const redirectToEmptyChild = route.routeConfig?.data?.redirectToEmptyChild || false;

    if (redirectToEmptyChild) {
      path = route.pathFromRoot.map(i => i.routeConfig?.path).filter(i => i).join('/');
    }

    this.isBreadcrumbHidden = route.routeConfig?.data?.hideBreadcrumb || false;

    // If route has hideBreadcrumb option as true, we stop building breadcrumbs and hide them
    // Can set this at a module level and all child pages will have breadcrumbs hidden
    // Will need to refactor if home page has no breadcrumbs
    if (this.isBreadcrumbHidden) {
      return;
    }

    // If the route is dynamic route such as ':id', remove it
    const lastRoutePart = path.split('/').pop();
    const isDynamicRoute = lastRoutePart.startsWith(':');
    if (isDynamicRoute && !!route.snapshot) {
      const paramName = lastRoutePart.split(':')[1];
      path = path.replace(lastRoutePart, route.snapshot.params[paramName]);
      label = route.snapshot.params[paramName];
    }

    // In the routeConfig the complete path is not available,
    // so we rebuild it each time
    let nextUrl = path ? `${url}/${path}` : url;

    if (redirectToEmptyChild) {
      nextUrl = path;
    }

    const breadcrumb: IBreadCrumb = {
      label,
      url: nextUrl,
      isCurrentPage: !route.firstChild
    };

    // Only adding route with non-empty label
    const newBreadcrumbs = breadcrumb.label ? [...breadcrumbs, breadcrumb] : [...breadcrumbs];
    if (!breadcrumb.isCurrentPage) {
      // If we are not on our current path yet,
      // there will be more children to look after, to build our breadcumb
      return this.buildBreadCrumb(route.firstChild, nextUrl, newBreadcrumbs);
    } else if (newBreadcrumbs?.length > 0 && breadcrumb.isCurrentPage && !breadcrumb.label) {
      // With feature routing modules, we will have an empty route at the base route of the feature routing module
      // Marking the pervious route as the current page here, so that it gets the disabled styling on UI as if its the current route.
      newBreadcrumbs[newBreadcrumbs.length - 1].isCurrentPage = true;
    }
    return newBreadcrumbs;
  }

  navigate(event: Event, url: string) {
    event.preventDefault();
    this.router.navigate([url]);
  }
}

export interface IBreadCrumb {
  label: string;
  url: string;
  isCurrentPage: boolean;
}
